import { type ReactNode, useMemo } from "react";
import type { RowData } from "@tanstack/react-table";
import { numberFormatter } from "@common/helpers/number";
import { Amount } from "@design-system/Amount/Amount";
import { resolveColumnMeta } from "../columns/columnKinds";
import { isDisplayColumnKind } from "../columns/columnLayout";
import type {
    DataGridColumnInstance,
    DataGridRow,
    DataGridTable,
} from "../columns/types";
import { getDisplayColumns } from "../focus/navigation";
import { getColumnStyle } from "../layout/columnVars";
import { getPinEdge } from "../layout/pinning";
import type { DataGridEditingOptions, DataGridTotals } from "../types";
import { InvalidCellTooltip } from "./InvalidCellTooltip";
import styles from "./Grid.module.scss";

/** Whether any visible column declares an `aggregate`. */
export const hasTotals = <TData extends RowData>(table: DataGridTable<TData>) =>
    table
        .getVisibleLeafColumns()
        .some((column) => column.columnDef.meta?.aggregate !== undefined);

const formatTotal = <TData extends RowData>({
    column,
    totals,
    rows,
}: {
    column: DataGridColumnInstance<TData>;
    totals: DataGridTotals;
    rows: Array<DataGridRow<TData>> | undefined;
}): ReactNode => {
    const meta = resolveColumnMeta(column.columnDef, column.id);
    const value =
        totals.source === "server"
            ? totals.values[column.id]
            : column.getAggregationValue(rows && { rows });
    if (typeof value !== "number" || !Number.isFinite(value)) {
        return null;
    }
    return meta.kind === "money" && meta.aggregate !== "count" ? (
        <Amount amount={value} showNegative />
    ) : (
        numberFormatter.format(value)
    );
};

interface TotalsRowProps<TData extends RowData> {
    table: DataGridTable<TData>;
    ariaRowIndex: number;
    totals: DataGridTotals;
    validateTotal: DataGridEditingOptions<TData>["validateTotal"];
    /** Separator rows, left out of client totals and `validateTotal`. */
    isSeparator: (row: TData) => boolean;
}

/**
 * Sticky bottom row with each `aggregate` column's total: the server's value,
 * or the sum over the filtered rows (all pages, before grouping). Money totals
 * use `Amount` like the column; the first data column without a total is
 * labelled "Total". `validateTotal` marks a total invalid with its message.
 * Separator rows never count.
 */
export const TotalsRow = <TData extends RowData>({
    table,
    ariaRowIndex,
    totals,
    validateTotal,
    isSeparator,
}: Readonly<TotalsRowProps<TData>>) => {
    const preGroupedModel = table.getPreGroupedRowModel();
    const totalRows = useMemo(
        () => preGroupedModel.rows.filter((row) => !isSeparator(row.original)),
        [preGroupedModel, isSeparator],
    );
    const validatedRows = useMemo(
        () => totalRows.map((row) => row.original),
        [totalRows],
    );
    const columns = getDisplayColumns(table);
    const labelColumnId = columns.find((column) => {
        const meta = resolveColumnMeta(column.columnDef, column.id);
        return !isDisplayColumnKind(meta.kind) && meta.aggregate === undefined;
    })?.id;
    const endStart = columns.length - table.getEndVisibleLeafColumns().length;

    const renderCell = (
        column: DataGridColumnInstance<TData>,
        index: number,
    ) => {
        const meta = resolveColumnMeta(column.columnDef, column.id);
        const pin = column.getIsPinned();
        const isLabel = column.id === labelColumnId;
        const invalidMessage =
            meta.aggregate === undefined
                ? undefined
                : validateTotal?.({
                      columnId: column.id,
                      rows: validatedRows,
                  });
        return (
            <div
                key={column.id}
                role="gridcell"
                aria-colindex={index + 1}
                className={styles.cell}
                style={getColumnStyle(column.id, pin)}
                data-column-id={column.id}
                data-kind={meta.kind}
                data-align={meta.align}
                data-pin={pin || undefined}
                data-pin-edge={getPinEdge(column)}
                data-invalid={invalidMessage !== undefined || undefined}
                aria-invalid={invalidMessage !== undefined || undefined}
            >
                <div className={styles.cellContent}>
                    {isLabel ? "Total" : null}
                    {meta.aggregate === undefined
                        ? null
                        : formatTotal({
                              column,
                              totals,
                              rows:
                                  totalRows.length ===
                                  preGroupedModel.rows.length
                                      ? undefined
                                      : totalRows,
                          })}
                </div>
                {invalidMessage === undefined ? null : (
                    <InvalidCellTooltip
                        message={invalidMessage}
                        isActive={false}
                    />
                )}
            </div>
        );
    };

    return (
        <div
            role="row"
            aria-rowindex={ariaRowIndex}
            className={styles.totalsRow}
            data-testid="data-grid-totals"
        >
            {columns.slice(0, endStart).map(renderCell)}
            <div className={styles.filler} aria-hidden />
            {columns
                .slice(endStart)
                .map((column, offset) => renderCell(column, endStart + offset))}
        </div>
    );
};
